<template>
  <div id="annual-forecast-view">
    <annual-forecast-side-view
      :isSidebarActive="sidebarActive"
      :data="sidebarData"
      :locations="locations"
      :elements="projectElements"
      :year="selectedYear"
      :projectId="projectId"
      @closeSidebar="toggleSidebar"
      @onForecastUpdated="forecastSaved"
      @onForecastInserted="forecastSaved"
    />

    <vx-card ref="annualCard" :title="$t('GradeControl.Forecast.Annual.Title')" class="forecast-list-filters mb-3" title-color="black" refresh-content-action @refresh="getForecasts">
      <vs-row vs-type="flex" vs-justify="space-between">
        <vs-col vs-type="flex" vs-justify="flex-start" vs-lg="2" vs-sm="6" vs-xs="12">
          <div class="vx-row">
            <div class="vx-col w-full">
              <form-label class="mr-2">{{ $t('GradeControl.Forecast.Annual.SelectYear') }}</form-label>
              <v-select :options="yearOptions" v-model="selectedYear" :clearable="false" class="w-full" />
            </div>
          </div>
        </vs-col>
        <vs-col vs-type="flex" vs-justify="flex-end" vs-align="center" vs-lg="6" vs-sm="12" vs-xs="12" class="mt-3">
          <vx-tooltip :text="$t('GradeControl.Forecast.Annual.AddNew')" position="bottom" delay=".3s">
            <feather-icon icon="PlusCircleIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="addNewData" />
          </vx-tooltip>
          <vx-tooltip :text="$t('Approve')" position="bottom" delay=".3s">
            <feather-icon icon="CheckCircleIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="openApprobationConfirm" />
          </vx-tooltip>
          <vx-tooltip :text="$t('GradeControl.Forecast.CancelApprobation')" position="bottom" delay=".3s">
            <feather-icon icon="XIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="openCancelApprobationConfirm" />
          </vx-tooltip>
          <vx-tooltip :text="$t('GradeControl.Forecast.Annual.Transpose')" position="bottom" delay=".3s">
            <feather-icon icon="LogOutIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="openTransposeConfirm" />
          </vx-tooltip>
          <vx-tooltip :text="$t('Delete')" position="bottom" delay=".3s">
            <feather-icon icon="TrashIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="openDeleteConfirm" />
          </vx-tooltip>
          <vx-tooltip :text="$t('GradeControl.Forecast.Annual.Upload')" position="bottom" delay=".3s">
            <feather-icon icon="UploadIcon" class="cursor-pointer ml-5" svg-classes="h-6 w-6" @click="openUploadPopup" />
          </vx-tooltip>
        </vs-col>
      </vs-row>
      <ag-grid-vue
        ref="agGridTable"
        :gridOptions="gridOptions"
        :context="context"
        class="ag-theme-material w-100 my-4 ag-grid-table"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="forecasts"
        rowSelection="multiple"
        :rowClassRules="rowClassRules"
        colResizeDefault="shift"
        :animateRows="true"
        :pagination="false"
        :suppressPaginationPanel="true"
        :enableCellChangeFlash="false"
        :enableRtl="$vs.rtl"
        @grid-ready="onGridReady"></ag-grid-vue>
    </vx-card>

    <!-- MRMR workbook upload (PBI 25564): all or nothing, the refused rows are listed in the popup. -->
    <vs-popup class="uploadPopup" :title="$t('GradeControl.Forecast.Annual.UploadFile', { year: selectedYear })" :active.sync="uploadPopupActive">
      <div class="p-6">
        <vs-row>
          <vs-col vs-w="12">
            <input id="annualUploadInputFile" type="file" accept=".xlsx,.xlsm" @change="getFile($event)">
            <div class="mb-1"></div>
            <small>{{ $t('GradeControl.Forecast.Annual.UploadHint') }}</small>
            <validation-message v-if="showInvalidFileError">{{ $t('GradeControl.Forecast.Annual.UploadInvalidFileError') }}</validation-message>
          </vs-col>
        </vs-row>
        <vs-row v-if="uploadErrors.length > 0" class="mt-4">
          <vs-col vs-w="12">
            <vs-alert color="danger" :title="$t('GradeControl.Forecast.Annual.UploadRefused')" active="true">
              <ul class="upload-errors">
                <li v-for="(message, index) in uploadErrors" :key="index">{{ message }}</li>
              </ul>
            </vs-alert>
          </vs-col>
        </vs-row>
        <div class="mt-6">
          <vs-row class="mb-3" vs-type="flex" vs-justify="flex-end">
            <vs-col vs-type="flex" vs-justify="center" vs-align="center" vs-w="12">
              <vs-button :disabled="!file || uploadInProgress" class="mr-5" @click="uploadFile">{{ $t('Submit') }}</vs-button>
              <vs-button :disabled="!file || uploadInProgress" type="border" color="danger" @click="cancelUpload">{{ $t('Cancel') }}</vs-button>
            </vs-col>
          </vs-row>
          <vs-row>
            <vs-col vs-w="12">
              <vs-progress v-show="uploadInProgress" indeterminate color="primary">primary</vs-progress>
            </vs-col>
          </vs-row>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import Vue from "vue";
import { AgGridVue } from "ag-grid-vue";
import vSelect from 'vue-select';
import { FormLabel, ValidationMessage } from "@/app/shared/components";
import ElementEnum from "@/app/shared/ElementEnum.js";
import SampleUnitEnum from "@/app/shared/SampleUnitEnum.js";
import MRMRResourceTypeEnum from "@/app/shared/MRMRResourceTypeEnum.js";
import LocationCellRenderer from "../../components/LocationCellRenderer.vue";
import AnnualForecastActionsCellRenderer from "./AnnualForecastActionsCellRenderer.vue";
import AnnualForecastSideView from "./AnnualForecastSideView.vue";
import { yearOptions, findResult, gradeOf, selectedIds, DEFAULT_ELEMENTS } from "./annualForecastRules.js";

const WORKBOOK_EXTENSIONS = ['.xlsx', '.xlsm'];

export default {
  name: "AnnualForecast",
  /* eslint-disable vue/no-unused-components */
  components: {
    AgGridVue,
    'v-select': vSelect,
    FormLabel,
    ValidationMessage,
    AnnualForecastSideView,
    AnnualForecastActionsCellRenderer,
    LocationCellRenderer
  },
  props: {
    projectId: {
      type: Number,
      required: true
    },
    locations: {
      type: Array,
      required: true
    }
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      selectedYear: currentYear,
      yearOptions: yearOptions(currentYear),
      projectElements: [],
      sidebarActive: false,
      sidebarData: {},
      uploadPopupActive: false,
      uploadInProgress: false,
      showInvalidFileError: false,
      uploadErrors: [],
      file: null,
      gridApi: null,
      gridOptions: {
        getRowNodeId: data => data.annualLocationForecastId
      },
      context: { componentParent: this },
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMenu: true
      },
      rowClassRules: {
        'forecast-approved': 'data.approbationDate !== null'
      }
    };
  },
  computed: {
    forecasts() {
      return this.$store.state.gradeControl.annualForecasts;
    },
    elementIds() {
      return this.projectElements.length > 0 ? this.projectElements : DEFAULT_ELEMENTS;
    },
    columnDefs() {
      // One column per element showing the grade: derived from the imported metal quantity and the tonnage, or
      // the grade typed by hand. The imported quantity itself is shown as a tooltip.
      const elementColumns = this.elementIds.map(element => ({
        headerName: ElementEnum.getNameFor(element),
        field: "results",
        flex: 1,
        minWidth: 95,
        maxWidth: 130,
        filter: 'agNumberColumnFilter',
        valueGetter: params => {
          const grade = this.gradeOfElement(params.data, element);
          return grade ? grade.value : null;
        },
        valueFormatter: params => this.formatGrade(params.data, element),
        tooltipValueGetter: params => this.formatImportedQuantity(params.data, element),
        comparator: (a, b) => this.compareNullable(a, b)
      }));

      return [
        {
          headerName: 'Actions',
          field: 'annualLocationForecastId',
          width: 150,
          filter: false,
          sortable: false,
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true,
          cellRendererFramework: Vue.extend(AnnualForecastActionsCellRenderer)
        },
        {
          headerName: this.$t('GradeControl.Location.Name'),
          field: 'locationId',
          flex: 2,
          minWidth: 180,
          filter: 'agTextColumnFilter',
          cellRendererFramework: Vue.extend(LocationCellRenderer),
          valueGetter: params => ({
            locationId: params.data.locationId,
            locationCode: this.locationCode(params.data.locationId),
            locationType: null,
            locations: this.locations
          }),
          filterValueGetter: params => this.locationCode(params.data.locationId),
          comparator: (a, b) => (a.locationCode || '').localeCompare(b.locationCode || '')
        },
        {
          headerName: this.$t('GradeControl.Forecast.Annual.Category'),
          field: 'resourceType',
          flex: 1,
          minWidth: 130,
          filter: 'agTextColumnFilter',
          valueGetter: params => this.resourceTypeName(params.data.resourceType)
        },
        {
          headerName: this.$systemUnits.getTonnageLabel(),
          field: 'tonnage',
          flex: 1,
          minWidth: 150,
          filter: 'agNumberColumnFilter',
          valueGetter: params => this.$systemUnits.convertTonnage(params.data.tonnage),
          valueFormatter: params => this.formatNumber(params.value)
        },
        {
          headerName: this.$systemUnits.getDensityLabel(),
          field: 'density',
          flex: 1,
          minWidth: 140,
          filter: 'agNumberColumnFilter',
          valueGetter: params => this.$systemUnits.convertDensityRounded(params.data.density)
        },
        ...elementColumns,
        {
          headerName: this.$t('GradeControl.Forecast.Annual.ApprovedBy'),
          field: 'approvedBy',
          flex: 2,
          minWidth: 240,
          filter: 'agTextColumnFilter'
        },
        {
          headerName: this.$t('GradeControl.Forecast.Annual.ApprobationDate'),
          field: 'approbationDate',
          flex: 1,
          minWidth: 180,
          valueFormatter: params => (params.value ? this.$options.filters.toDate(params.value) : '')
        }
      ];
    }
  },
  watch: {
    selectedYear() {
      this.getForecasts();
    },
    projectId() {
      this.loadProjectElements();
      this.getForecasts();
    }
  },
  created() {
    this.loadProjectElements();
    this.getForecasts();
  },
  beforeDestroy() {
    this.$store.dispatch("gradeControl/clearAnnualForecastListItems");
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
    },
    loadProjectElements() {
      this.$cqrsGet("core/projectElements/Find", { params: { projectId: this.projectId } })
        .then((response) => {
          this.projectElements = response.data.model || [];
        })
        .catch(() => {
          this.projectElements = [];
        });
    },
    getForecasts() {
      this.$vs.loading();
      this.$store.dispatch("gradeControl/fetchAnnualForecastListItems", {
        projectId: this.projectId,
        year: this.selectedYear
      })
        .catch(() => {})
        .finally(() => {
          this.$vs.loading.close();
          if (this.$refs.annualCard) {
            this.$refs.annualCard.removeRefreshAnimation();
          }
        });
    },

    // Formatting
    locationCode(locationId) {
      const location = this.locations.find(l => l.locationId === locationId);
      return location ? location.code : '';
    },
    resourceTypeName(resourceType) {
      return resourceType === null || resourceType === undefined ? '' : MRMRResourceTypeEnum.getNameFor(resourceType);
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '';
      const number = Number(value);
      return Number.isInteger(number) ? number.toString() : number.toFixed(2);
    },
    gradeOfElement(forecast, element) {
      return gradeOf(findResult(forecast.results, element), forecast.tonnage);
    },
    formatGrade(forecast, element) {
      const grade = this.gradeOfElement(forecast, element);
      if (!grade) return '';
      return `${this.formatNumber(grade.value)} ${SampleUnitEnum.getNameFor(grade.unit)}`.trim();
    },
    formatImportedQuantity(forecast, element) {
      const result = findResult(forecast.results, element);
      if (!result || result.quantity === null || result.quantity === undefined) return '';
      return `${this.$t('GradeControl.Forecast.Annual.ImportedQuantity')} : ${this.formatNumber(result.quantity)} ${SampleUnitEnum.getNameFor(result.quantityUnit)}`;
    },
    compareNullable(a, b) {
      if (a === null && b === null) return 0;
      if (a === null) return -1;
      if (b === null) return 1;
      return a - b;
    },

    // Side view
    toggleSidebar(val = false) {
      this.sidebarActive = val;
    },
    addNewData() {
      this.sidebarData = {};
      this.toggleSidebar(true);
    },
    editData(forecast) {
      this.sidebarData = forecast;
      this.toggleSidebar(true);
    },
    forecastSaved() {
      this.notifySuccess(this.$t("SaveSuccess"));
      this.toggleSidebar(false);
      this.getForecasts();
    },

    // Upload
    openUploadPopup() {
      this.file = null;
      this.uploadErrors = [];
      this.showInvalidFileError = false;
      this.uploadPopupActive = true;
    },
    getFile(event) {
      const selected = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null;
      this.uploadErrors = [];
      if (selected && !WORKBOOK_EXTENSIONS.some(extension => selected.name.toLowerCase().endsWith(extension))) {
        this.showInvalidFileError = true;
        this.file = null;
        return;
      }
      this.showInvalidFileError = false;
      this.file = selected;
    },
    cancelUpload() {
      // Nothing was sent: the file is simply dropped and the popup closed.
      this.file = null;
      this.uploadErrors = [];
      this.uploadPopupActive = false;
    },
    uploadFile() {
      if (!this.file) return;
      this.uploadInProgress = true;
      this.uploadErrors = [];
      this.$store.dispatch("gradeControl/uploadAnnualForecasts", {
        projectId: this.projectId,
        year: this.selectedYear,
        file: this.file
      })
        .then((response) => {
          const model = response.data.model || {};
          this.notifySuccess(this.$t("GradeControl.Forecast.Annual.UploadSuccess", {
            forecasts: model.forecastsCreated,
            locations: model.locationsCreated,
            year: model.year
          }));
          this.uploadPopupActive = false;
          this.file = null;
          this.$emit("locationsChanged");
          this.getForecasts();
        })
        .catch((error) => {
          const data = error && error.response ? error.response.data : null;
          this.uploadErrors = data && Array.isArray(data.messages) ? data.messages.map(m => m.key) : [];
        })
        .finally(() => {
          this.uploadInProgress = false;
        });
    },

    // Batch actions
    selectedForecastIds() {
      return this.gridApi ? selectedIds(this.gridApi.getSelectedRows()) : [];
    },
    confirmOnSelection(text, accept) {
      if (this.selectedForecastIds().length === 0) {
        this.$vs.dialog({
          color: 'danger',
          title: this.$t('GradeControl.Forecast.Annual.Title'),
          text: this.$t('GradeControl.Forecast.Annual.NoForecastSelected')
        });
        return;
      }
      this.$vs.dialog({
        type: "confirm",
        color: "warning",
        title: this.$t('GradeControl.Forecast.Annual.Title'),
        text,
        accept
      });
    },
    openApprobationConfirm() {
      this.confirmOnSelection(this.$t('GradeControl.Forecast.Annual.ConfirmApprobation'), this.approve);
    },
    openCancelApprobationConfirm() {
      this.confirmOnSelection(this.$t('GradeControl.Forecast.Annual.ConfirmCancelBatchApprobation'), () => this.cancelApprobation(this.selectedForecastIds()));
    },
    openTransposeConfirm() {
      this.confirmOnSelection(this.$t('GradeControl.Forecast.Annual.ConfirmTranspose', { year: this.selectedYear + 1 }), this.transpose);
    },
    openDeleteConfirm() {
      this.confirmOnSelection(this.$t('GradeControl.Forecast.Annual.ConfirmDeleteMultiple'), () => this.deleteMany(this.selectedForecastIds()));
    },
    approve() {
      this.runBatch("gradeControl/approveAnnualForecasts", { ids: this.selectedForecastIds() }, this.$t("GradeControl.Forecast.Annual.ApprobationCompleted"));
    },
    cancelApprobation(ids) {
      this.runBatch("gradeControl/cancelAnnualForecastsApprobation", { ids }, this.$t("GradeControl.Forecast.Annual.CancellationCompleted"));
    },
    transpose() {
      const targetYear = this.selectedYear + 1;
      this.runBatch("gradeControl/transposeAnnualForecasts", { ids: this.selectedForecastIds(), targetYear }, this.$t("GradeControl.Forecast.Annual.TransposeCompleted", { year: targetYear }));
    },
    deleteMany(ids) {
      this.runBatch("gradeControl/removeAnnualForecastBatch", { ids }, this.$t("GradeControl.Forecast.Annual.ForecastDeleted"));
    },
    runBatch(action, payload, successText) {
      this.$vs.loading();
      this.$store.dispatch(action, Object.assign({ projectId: this.projectId }, payload))
        .then(() => {
          this.notifySuccess(successText);
          this.getForecasts();
        })
        .catch(() => {})
        .finally(() => {
          this.$vs.loading.close();
        });
    },
    notifySuccess(text) {
      this.$vs.notify({
        title: this.$t("Success"),
        text,
        color: "success",
        iconPack: 'feather',
        icon: 'icon-check'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.upload-errors {
  margin-top: 0.5rem;
  padding-left: 1.2rem;
  max-height: 16rem;
  overflow-y: auto;

  li {
    list-style: disc;
    margin-bottom: 0.25rem;
  }
}
</style>
